import React, { useState, useEffect } from "react"
import { Form, Input, Button, Radio, message, Checkbox } from "antd"
import PanelLayout from "../../Layout/PanelLayout"
import SelectBox from "../../components/SelectBox/SelectBox"
import apiClient from "../../Utils/apiClient"
import { useNavigate, useParams } from "react-router-dom"

const { TextArea } = Input

const CustomTaskForm = () => {
  const [form] = Form.useForm()
  const { id } = useParams()
  const isEditMode = !!id
  const [targetType, setTargetType] = useState("all")
  const [groupId, setGroupId] = useState([])
  const [customTaskType, setCustomTaskType] = useState("")
  const [deviceIds, setDeviceIds] = useState([])
  const [btnDisabled, setBtnDisabled] = useState(false)
  const navigate = useNavigate()

  const handleFinish = async (values) => {
    setBtnDisabled(true)
    const payload = {
      title: values.title?.trim(),
      targetType: values.targetType,
      groups: values.targetType === "group" ? values.groups || [] : [],
      devices: values.targetType === "specific" ? values.devices || [] : [],
      deviceStatuses:
        values.targetType === "specific" ? [] : values.deviceStatuses || [],
      taskType: "custom-task",
      customTaskType: values.customTaskType,
      customScript:
        values.customTaskType === "custom"
          ? String(values.customScript || "").trim()
          : "",
      customTasks:
        values.customTaskType === "select" ? values.customTasks || [] : [],
    }

    try {
      let result
      if (isEditMode) {
        result = await apiClient(
          `tasks/custom-task/update-byId/${id}`,
          "POST",
          payload
        )
      } else {
        result = await apiClient("tasks/custom-task/create", "POST", payload)
      }

      if (result?.success) {
        message.success(result.message || "Custom Task saved successfully")
        navigate("/custom-task")
      } else {
        message.error(result?.message || "Failed to save custom task")
      }
    } catch (err) {
      console.error("Custom Task submit failed", err)
      message.error("Something went wrong. Please try again.")
    } finally {
      setBtnDisabled(false)
    }
  }

  const getData = async (taskId) => {
    try {
      const res = await apiClient(`tasks/custom-task/get-byId/${taskId}`, "POST")
      if (res?.success && res.task) {
        const task = res.task
        form.setFieldsValue({
          title: task.title,
          targetType: task.targetType,
          groups: task.groups,
          devices: task.devices,
          deviceStatuses: task?.deviceStatuses,
          customScript: task.customScript,
          customTaskType: task?.customTaskType,
          customTasks: task?.customTasks,
        })
        setTargetType(task.targetType)
        setCustomTaskType(task?.customTaskType)
        setGroupId(task.groups || [])
        setDeviceIds(task.devices || [])
      }
    } catch (err) {
      console.error("Error fetching task by ID", err)
    }
  }

  useEffect(() => {
    if (isEditMode) {
      getData(id)
    }
  }, [id])

  useEffect(() => {
    if (targetType === "group") {
      setDeviceIds([])
      form.setFieldsValue({ devices: [] })
    }
    if (targetType === "specific") {
      setGroupId([])
      form.setFieldsValue({ groups: [], deviceStatuses: [] })
    }
  }, [targetType])

  return (
    <PanelLayout
      title={isEditMode ? "Edit Custom Task" : "Create Custom Task"}
      subtitle="Target devices and define the command or script to run."
    >
      <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ targetType: "all" }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter your Title" }]}
          >
            <Input placeholder="Enter Title Here" />
          </Form.Item>
          <Form.Item
            name="targetType"
            label="Target Type"
            rules={[{ required: true, message: "Select a target type" }]}
          >
            <Radio.Group onChange={(e) => setTargetType(e.target.value)}>
              <Radio value="all">All Devices</Radio>
              <Radio value="group">Group</Radio>
              <Radio value="specific">Specific Devices</Radio>
            </Radio.Group>
          </Form.Item>

          {targetType === "group" && (
            <Form.Item
              name="groups"
              label="Select Groups"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: "Select at least one group",
                },
              ]}
            >
              <SelectBox
                className="w-100 mb-3"
                name="name"
                endPoint="group-devices"
                placeholder="Search Device Groups"
                value={groupId}
                multiple
                onSelect={(value) => setGroupId(value)}
              />
            </Form.Item>
          )}

          {targetType === "specific" && (
            <Form.Item
              name="devices"
              label="Select Devices"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: "Select at least one device",
                },
              ]}
            >
              <SelectBox
                className="w-100 mb-3"
                name="hostname"
                multiple
                endPoint="devices/identifiers"
                placeholder="Search Devices"
                value={deviceIds}
                onSelect={(value) => setDeviceIds(value)}
              />
            </Form.Item>
          )}
          {(targetType === "group" || targetType === "all") && (
            <Form.Item name="deviceStatuses" label="Select Statuses">
              <Checkbox.Group
                options={[
                  { label: "Online", value: "Online" },
                  { label: "Offline", value: "Offline" },
                  { label: "Away", value: "Away" },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item
            name="customTaskType"
            label="Select Custom Task Type"
            rules={[
              { required: true, message: "Select custom or saved script" },
            ]}
          >
            <Radio.Group
              onChange={(e) => {
                const nextType = e.target.value
                setCustomTaskType(nextType)
                if (nextType === "custom") {
                  form.setFieldsValue({ customTasks: [] })
                } else {
                  form.setFieldsValue({ customScript: "" })
                }
              }}
            >
              <Radio value="custom">Custom</Radio>
              <Radio value="select">Select From Table</Radio>
            </Radio.Group>
          </Form.Item>
          {customTaskType === "custom" && (
            <Form.Item
              name="customScript"
              label="Custom Script"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please enter your script/command",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Enter your custom script/command..."
              />
            </Form.Item>
          )}
          {customTaskType === "select" && (
            <Form.Item
              name="customTasks"
              label="Select Custom Task"
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: "Select at least one saved script",
                },
              ]}
            >
              <SelectBox
                className="w-100 mb-3"
                name="title"
                optionValue="_id"
                endPoint="scripts"
                multiple
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" disabled={btnDisabled}>
              {isEditMode ? "Update Task" : "Save Task"}
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              htmlType="button"
              onClick={() => {
                form.resetFields()
                setTargetType("all")
                setCustomTaskType("")
                setGroupId([])
                setDeviceIds([])
              }}
            >
              Reset
            </Button>
          </Form.Item>
        </Form>
    </PanelLayout>
  )
}

export default CustomTaskForm
